/**
 * @module AuthPanel
 * @description
 * Renders the authentication interface for the STREEN application.
 *
 * The component provides login and registration forms, QR-code login, and QR-code display for registered users when applicable.
 *
 * Authentication requests are handled through the authentication context and backend API. The component manages form input, loading
 * states, validation feedback, QR-code scanning, and authentication results. It also supports modal visibility controlled by its parent component.
 *
 * @param {Object} props
 * @param {boolean} props.visible - Determines whether the authentication
 *   modal is visible.
 * @param {Function} props.onCancel - Callback invoked when the modal
 *   should be closed.
 *
 * @requires react
 * @requires antd
 * @requires @ant-design/icons
 * @requires axios
 * @requires module:../context/authContext
 * @requires react-qr-reader
 * @requires qrcode.react
 */

import React, { useState, useRef, useEffect } from "react";
import { Modal, Form, Input, Button, Tabs, message, Alert, Select } from "antd";
import AuthContext from "../context/authContext";
import { useContext } from "react";
import axios from "axios";
import Notifications from "../components/Notifications";
import Messages from "../components/Messages";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import streenLogo from "../img/logo.png";
import streenLogoMoto from "../img/logo.png";
import { LoginOutlined, QrcodeOutlined, RocketOutlined, UserOutlined, LockOutlined, PrinterOutlined, } from "@ant-design/icons";
import { QrReader } from "react-qr-reader";
import { QRCodeCanvas } from "qrcode.react";

const verbose = false; // set to true to enable debug logging
const vlog = (...args) => verbose && console.log(...args);
const { TabPane } = Tabs;

/**
 * Authentication modal for STREEN.
 *
 * Provides QR-code login and username/password login, as well as  user registration and QR login-card generation for administrators.
 *
 * @param {Object} props
 * @param {boolean} props.visible - Controls whether the modal is visible.
 * @param {Function} props.onCancel - Closes the authentication modal.
 */
const AuthPanel = ({ visible, onCancel }) => {
  const { currentUser, logout } = useContext(AuthContext); // Retrieve currentUser
  const [activeTab, setActiveTab] = useState("qr");
  const [loading, setLoading] = useState(false);
  const [loginInputs, setLoginInputs] = useState({ username: "ben", password: "ben", });
  const [registerInputs, setRegisterInputs] = useState({ username: "", email: "", password: "", });
  const { login } = useContext(AuthContext);
  const [err, setError] = useState(null);
  const [authForm] = Form.useForm();
  const [focusedInput, setFocusedInput] = useState(null); // State to track focused input
  const [showKeyboard, setShowKeyboard] = useState(false);
  const { showErrorMessage } = Messages();
  const [myQr, setMyQr] = useState(null);
  const lastQrRef = useRef(null);
  const [scannerEnabled, setScannerEnabled] = useState(true);
  const [newCredentials, setNewCredentials] = useState(null);
  const [useCustomPassword, setUseCustomPassword] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [users, setUsers] = useState([]);

  // Retrieve user data when an authenticated user is available.
  useEffect(() => {
    if (!currentUser) return; // only fetch if logged in
    axios
      .get("/api/users", { withCredentials: true })
      .then((res) => setUsers(res.data))
      .catch((err) => {
        if (err.response?.status !== 401) console.error(err);
      });
  }, [currentUser]);

  // Reset QR scanning when the authentication modal becomes visible.
  useEffect(() => {
    vlog("visible changed", visible);
    if (visible) {
      lastQrRef.current = null;
      setScannerEnabled(true);
    }
  }, [visible]);

  // Retrieve the administrator's QR login code after authentication.
  useEffect(() => {
    if (currentUser) {
      if (currentUser?.role === 0) {
        axios
          .get("/api/auth/my-qr")
          .then((res) => setMyQr(res.data.qr_code))
          .catch((err) => console.error(err));
      }
    }
  }, [currentUser]);

 /**
 * Toggles custom password entry during user registration.
 * Clears the custom password when switching back to automatic generation.
 */
  const toggleCustomPassword = () => {
    setUseCustomPassword((prev) => !prev);

    // clear password if toggled off
    if (useCustomPassword) {
      setRegisterInputs((prev) => ({ ...prev, password: "" }));
    }
  };

 /**
 * Processes a QR-code login attempt.
 *
 * Prevents duplicate scans, temporarily disables the scanner, authenticates using the QR token, and closes the modal on success. Re-enables scanning if authentication fails.
 *
 * @param {string} text - Decoded QR-code content.
 */
  const handleQrResult = async (text) => {
    if (!text) return;
    if (!scannerEnabled) return;
    if (lastQrRef.current === text) return;

    lastQrRef.current = text;
    setScannerEnabled(false);

    try {
      await login({ qr: text, type: "qr" });
      onCancel();
    } catch {
      lastQrRef.current = null;
      setScannerEnabled(true);
    }
  };

/**
 * Displays the on-screen keyboard and records the focused input field.
 *
 * @param {Event} e - Input focus event.
 */
  const handleInputFocus = (e) => {
    setShowKeyboard(true);
    setFocusedInput(e.target.name); // Set focused input
  };
  // Refs for the input fields
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

/**
 * Updates the currently focused login field using on-screen keyboard input. Restores focus to the corresponding input element.
 *
 * @param {string} input - Current keyboard input value.
 */
  const handleKeyboardInputChange = (input) => {
    setLoginInputs({ ...loginInputs, [focusedInput]: input }); // Update the focused input value

    vlog("focusedInput:", focusedInput);
    vlog("loginInputs", loginInputs);
    if (focusedInput === "username" && usernameRef.current) {
      usernameRef.current.focus();
    } else if (focusedInput === "password" && passwordRef.current) {
      passwordRef.current.focus();
    }
  };

/**
 * Updates the username or password in the login state.
 *
 * @param {Event} e - Input change event.
 */
  const handleLoginChange = (e) => {
    setLoginInputs({ ...loginInputs, [e.target.name]: e.target.value });
  };

/**
 * Authenticates the user with the supplied login credentials.
 *
 * Closes the modal after successful authentication and displays an error message if authentication fails.
 *
 * @param {Object} values - Values submitted by the Ant Design form.
 */
  const handleLogin = async (e) => {
    // e.preventDefault();
    setLoading(true);
    vlog("loginInputs", loginInputs);
    try {
      await login(loginInputs);
      onCancel();

      setTimeout(() => {
        //   message.success('Logged in successfully!');
        Notifications({
          placement: "top",
          message: `willkommen ${loginInputs.username}!`,
          description: "Erfolgreich eingeloggt!",
        });
        setLoading(false);
        onCancel(); // Close the modal on success
      }, 1000);
    } catch (error) {
      const msg =
        error.response?.data ||
        error.message ||
        "Unbekannter Fehler beim Login";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

/**
 * Updates registration form state when a field changes.
 *
 * @param {Event} e - Input change event.
 */
  const handleRegistrationChange = (e) => {
    setRegisterInputs((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

/**
 * Registers a new user.
 *
 * Optionally removes the password from the payload to allow the backend
 * to generate one automatically. Stores the returned QR code and
 * credentials and opens the generated QR-code tab.
 *
 * @param {Object} values - Values submitted by the registration form.
 */
  const handleRegister = async (values) => {
    setLoading(true);
    try {
      const payload = { ...registerInputs };

      // if not using custom password → remove it
      if (!useCustomPassword) {
        delete payload.password;
      }

      const res = await axios.post("/api/auth/register", payload);
      const qrCode = res.data.qr_code; // backend returns the token
      setMyQr(qrCode); // save QR for display

      setNewCredentials({
        username: res.data.username,
        password: res.data.generated_password || registerInputs.password,
      });

      setActiveTab("myQr"); // switch to QR code tab
      setLoading(false);
      message.success("Registered successfully!");
    } catch (err) {
      setError(err.response?.data || "Registration failed");
      setLoading(false);
    }
  };

  return (
    <>
     {/* Authentication modal; hidden while loading */}
      {!loading && (
        <Modal
          open={visible}
          onCancel={onCancel}
          footer={null}
          width={currentUser?.role === 0 ? "85%" : "65%"}
        >
           {/* STREEN logo and modal header */}
          <div style={{ textAlign: "center", marginBottom: 30, }}>
            <img
              src={streenLogoMoto}
              alt="STREEN"
              style={{
                width: 200,
                marginBottom: 8,
              }}
            />
          </div>

          <div className="auth-panel">
            <Tabs activeKey={activeTab} onChange={setActiveTab} centered>
              {/* Username/password authentication */}
              <Tabs.TabPane
                key="login"
                tab={
                  <span className="auth-tab-label">
                    <LoginOutlined className="auth-tab-icon" />
                    <span>Einloggen</span>
                  </span>
                }
              >
                <div className="auth">
                  <Form
                    form={authForm}
                    name="login"
                    onFinish={handleLogin}
                    initialValues={{ remember: true }}
                  >
                    <Form.Item
                      label={
                        <span className="auth-field-label">
                          <UserOutlined />
                          <span className="auth-field-text">Benutzername</span>
                        </span>
                      }
                      name="username"
                      layout="vertical"
                      rules={[
                        {
                          message: "😕 Bitte Benutzername eingeben!",
                        },
                      ]}
                      style={{ paddingBottom: 24 }}
                    >
                      <Input
                        className="auth-form-input"
                        placeholder="z.B. Ben"
                        size="large"
                        name="username"
                        value={loginInputs.username}
                        defaultValue={loginInputs.username}
                        onChange={handleLoginChange}
                        // onFocus={handleInputFocus}
                        ref={usernameRef}
                      />
                    </Form.Item>
                    <Form.Item
                      label={
                        <span className="auth-field-label">
                          <LockOutlined />
                          <span className="auth-field-text">Passwort</span>
                        </span>
                      }
                      name="password"
                      layout="vertical"
                      rules={[{ message: "😕 Bitte Passwort eingeben!" }]}
                      style={{ paddingBottom: 24 }}
                    >
                      <Input.Password
                        className="auth-form-input"
                        placeholder="••••••••"
                        size="large"
                        name="password"
                        value={loginInputs.password}
                        defaultValue={loginInputs.username}
                        onChange={handleLoginChange}
                        // onFocus={handleInputFocus}
                        ref={passwordRef}
                      />
                    </Form.Item>
                    <Form.Item style={{ textAlign: "center", paddingBottom: 30 }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        disabled={!loginInputs.username}
                        loading={loading}
                        size="large"
                        className="auth-login-button"
                      >
                        <span className="auth-login-button-content">
                          <RocketOutlined className="auth-login-button-icon" />
                          <span>Los geht’s</span>
                        </span>
                      </Button>
                    </Form.Item>
                    {err && (
                      <Alert
                        style={{ marginTop: 16 }}
                        message="😕 Ups!"
                        description="Der Benutzername oder das Passwort stimmt nicht."
                        type="error"
                        showIcon
                      />
                    )}
                  </Form>
                  {showKeyboard && (
                    <Keyboard
                      onChange={handleKeyboardInputChange}
                      inputName={focusedInput} // Pass the focused input as inputName
                    />
                  )}
                </div>
              </Tabs.TabPane>

              {/* QR-code authentication using the device camera */}
              <Tabs.TabPane
                key="qr"
                tab={
                  <span className="auth-tab-label">
                    <QrcodeOutlined className="auth-tab-icon" />
                    <span>QR Login</span>
                  </span>
                }
              >
                <div className="auth auth-qr">
                  <div className="auth-qr-instructions">
                    <UserOutlined className="auth-qr-instructions-icon" />
                    <p>Scanne deinen STREEN QR-Code!</p>
                  </div>

                  <div className="auth-qr-reader">
                    {scannerEnabled && (
                      <QrReader
                        constraints={{ facingMode: "environment" }}
                        onResult={(result) => {
                          if (result?.text) handleQrResult(result.text);
                        }}
                      />
                    )}

                    <div className="auth-qr-overlay" />
                  </div>

                  {err && (
                    <Alert
                      className="auth-qr-error"
                      message="😕 QR-Code nicht erkannt"
                      type="error"
                      showIcon
                    />
                  )}
                </div>
              </Tabs.TabPane>


            </Tabs>
          </div>
        </Modal>
      )}
    </>
  );
};

export default AuthPanel;
